import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import dayjs from 'dayjs'

import { landAxios } from '@Root/utils/landAxios'
import MyhouseTrendPrice from '@Types/entities/MyhouseTrendPrice'
import { formatMoney, formatMoneyBasedOnWon, MONEY_FORMAT_TYPE } from '@Root/utils/moneyFormatter'
import LandPriceTotal from '@Types/entities/LandPriceTotal'
import LandPrice from '@Types/entities/LandPrice'

export const fetchTrendPrice = createAsyncThunk('/myhouse/fetchTrendPrice', async (houseNo: number) => {
  return await (await landAxios.get(`/myhouse/trendPrice/${houseNo}`)).data
})

export const fetchLandPrice = createAsyncThunk('/myhouse/fetchLandPrice', async (param: UnitLandPriceParam) => {
  return await (
    await landAxios.get(`/landPrice/hscp/detail`, {
      params: { hscp: param.complexNo, bild: param.bildNo, ho: param.hoNo },
    })
  ).data
})

export type UnitPriceState = {
  trendPrice: MyhouseTrendPrice | null
  trendPriceDetail: TrendPriceDetail | null
  landPrice: LandPriceTotal | null
  landPriceDetail: LandPriceDetail | null
}

export interface TrendPriceDetail {
  basisDate: string
  prevYear: number
  dealLowerLimitPrice: string
  dealUpperLimitPrice: string
  monthlyPriceChangePercent: number
}

export interface LandPriceDetail {
  basisDate: string
  prevYear: number
  landPrice: string
  growthRatioYearly: number | null
  tax: string
}

export interface UnitLandPriceParam {
  complexNo: string
  bildNo: string
  hoNo: string
}

export const initialState: UnitPriceState = {
  landPrice: null,
  landPriceDetail: null,
  trendPrice: null,
  trendPriceDetail: null,
  isTrendPriceExist: false,
} as UnitPriceState

export const unitPriceSlice = createSlice({
  name: 'unitPrice',
  initialState,
  reducers: {
    resetUnitTrendPrice: (state: UnitPriceState) => {
      state.trendPrice = null
      state.trendPriceDetail = null
    },
    resetUnitLandPrice: (state: UnitPriceState) => {
      state.landPrice = null
      state.landPriceDetail = null
    },
  },

  extraReducers: {
    [fetchTrendPrice.fulfilled.type]: (state: UnitPriceState, action: PayloadAction<MyhouseTrendPrice>) => {
      const unitTrendPrice: MyhouseTrendPrice = action.payload
      state.trendPrice = action.payload
      state.trendPriceDetail = makeTrendPriceDetail(unitTrendPrice)
    },
    [fetchLandPrice.fulfilled.type]: (state: UnitPriceState, action: PayloadAction<LandPriceTotal>) => {
      if (action.payload?.landPrices[0]) {
        const unitLandPrice: LandPrice = action.payload.landPrices[0]
        state.landPrice = action.payload
        state.landPriceDetail = makeLandPriceDetail(unitLandPrice)
      } else {
        state.landPrice = null
        state.landPriceDetail = null
      }
    },
  },
})

function makeTrendPriceDetail(unitTrendPrice: MyhouseTrendPrice): TrendPriceDetail {
  const trendPriceDetail: TrendPriceDetail = {} as TrendPriceDetail
  trendPriceDetail.basisDate = dayjs(unitTrendPrice.basisDate).format('YYYY.MM.DD')

  if (unitTrendPrice.dealLowerLimitPrice > 0) {
    trendPriceDetail.dealLowerLimitPrice = formatMoney(Math.abs(unitTrendPrice.dealLowerLimitPrice), MONEY_FORMAT_TYPE.MOBILE)
  }
  if (unitTrendPrice.dealUpperLimitPrice > 0) {
    trendPriceDetail.dealUpperLimitPrice = formatMoney(Math.abs(unitTrendPrice.dealUpperLimitPrice), MONEY_FORMAT_TYPE.MOBILE)
  }

  const dealAverageChangePriceMonthly = unitTrendPrice.dealAverageChangePriceMonthly
  if (dealAverageChangePriceMonthly != null) {
    const averageDealPriceThisMonth = (unitTrendPrice.dealLowerLimitPrice + unitTrendPrice.dealUpperLimitPrice) / 2
    const averageDealPriceLastMonth = averageDealPriceThisMonth - dealAverageChangePriceMonthly
    trendPriceDetail.monthlyPriceChangePercent = Math.round(((averageDealPriceThisMonth - averageDealPriceLastMonth) / averageDealPriceLastMonth) * 100)
  }
  return trendPriceDetail
}

function makeLandPriceDetail(unitLandPrice: LandPrice): LandPriceDetail {
  const landPriceDetail: LandPriceDetail = {} as LandPriceDetail
  const landPrice = Number(unitLandPrice.price)
  const prevLandPrice = Number(unitLandPrice.prevPrice)

  landPriceDetail.basisDate = dayjs(unitLandPrice.stdYmd.toString()).format('YYYY.MM.DD')
  landPriceDetail.prevYear = unitLandPrice.stdYear - 1

  landPriceDetail.landPrice = formatMoneyBasedOnWon(landPrice, MONEY_FORMAT_TYPE.LITTLE).replace('억', '억 ')
  if (prevLandPrice !== 0) {
    landPriceDetail.growthRatioYearly = Math.round(((landPrice - prevLandPrice) / prevLandPrice) * 100)
  } else {
    landPriceDetail.growthRatioYearly = null
  }

  const propertyTotalTax = Number(unitLandPrice.landPriceTax.propertyTotalTax)
  const realEstateTotalTax = Number(unitLandPrice.landPriceTax.realEstateTotalTax)
  landPriceDetail.tax = formatMoneyBasedOnWon(propertyTotalTax + realEstateTotalTax, MONEY_FORMAT_TYPE.TAX)

  return landPriceDetail
}

export const { resetUnitTrendPrice, resetUnitLandPrice } = unitPriceSlice.actions
export default unitPriceSlice.reducer
